/**
 * The cross-pane query request — "show me THIS platform's records of THAT
 * kind" (REQ-UI-189).
 *
 * # Why this exists
 *
 * Every SAPIENT type the catalog registers carries ``producer_id``, so a
 * platform's status messages, detections and raw-blob announcements have
 * always been queryable. What was missing was a way to *get there*: an
 * operator looking at a platform's RECENT MESSAGES counts had to open the
 * Query panel, pick the type, add a predicate and name the platform again —
 * four steps to re-state something already on screen. The counts said "47
 * status messages in the last 60 s" and offered no way to look at them.
 *
 * # Why a store rather than a selection
 *
 * The selection bus (``selection/selection-store``) answers "what is the
 * operator inspecting". This answers something narrower — the last thing a
 * pane asked the Query panel to show — and putting it there would make every
 * consumer of a selection have to ignore it.
 *
 * # Latest-request, not a consumed queue
 *
 * The store holds the **most recent** request and never clears it; the Query
 * panel remembers which one it has already applied. That is what lets the
 * apply happen *during render*, the pattern this panel already uses for its
 * type-change reset and the one ``provider-editor.tsx`` documents: an effect
 * would have to call ``setState`` after a committed render, which is the
 * cascade ``react-hooks/set-state-in-effect`` exists to catch, and whose
 * waiver list the project treats as an inventory to shrink.
 *
 * Two consequences worth stating:
 *
 * - **Re-asking works.** :func:`requestQuery` stores a fresh object, so
 *   clicking the same link again after editing the query re-applies it
 *   rather than reading as a no-op.
 * - **A reopened Query panel restores the last request** rather than coming
 *   back empty, because a fresh panel has applied nothing yet. That is the
 *   behaviour an operator who closed and reopened the panel would expect of
 *   a surface that otherwise has no memory.
 *
 * Cross-references:
 *   - REQ-UI-189 (the handoff), REQ-UI-188 (the platform picker it lands in),
 *     REQ-UI-051 (the map→list reveal this follows).
 *   - ADR-024 §5 (cross-pane coordination); ADR-038 §Decision 5.
 */
import { create } from "zustand";

/**
 * One predicate to seed, as an attribute name and the value to match.
 *
 * The attribute is named rather than typed as a known key because the catalog
 * is the authority on what a type carries (REQ-QUERY-003) — a union of
 * attribute names here would have to be regenerated whenever a type gained one,
 * and the panel already has to tolerate an attribute the caller's role cannot
 * see (`visible_to` REMOVES it rather than flagging it, REQ-AUTH-076).
 */
export interface QueryHandoffPredicate {
  /** The attribute's wire name as the catalog registers it (e.g. ``reason``). */
  readonly attribute: string;
  /** The value to match. Equality only — this is a link, not a query builder. */
  readonly value: string;
}

/** One "open the Query panel, asking this" request. */
export interface QueryHandoff {
  /** The queryable type id to select (e.g. ``sapient_status``). */
  readonly typeId: string;
  /**
   * The predicates to seed, ANDed. Was a single ``producerId`` until
   * 2026-10-06, when the quarantine breakdown needed to hand off a ``reason``
   * as well (REQ-STATUS-100): one general mechanism rather than a second
   * field per call site, so the panel keeps exactly one seeding path.
   *
   * Empty is legal and means "just switch to this type".
   */
  readonly predicates: readonly QueryHandoffPredicate[];
}

export interface QueryHandoffState {
  /** The most recent request, or ``null`` when none has been made. */
  readonly request: QueryHandoff | null;
  /** Ask the Query panel to show ``handoff``, replacing any earlier request. */
  readonly requestQuery: (handoff: QueryHandoff) => void;
}

export const useQueryHandoffStore = create<QueryHandoffState>((set) => ({
  request: null,
  requestQuery: (handoff) => set({ request: handoff }),
}));
